import React, { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Viewport } from '../WorldCanvas/WorldCanvas';
import { ZONE_FREE_RADIUS } from './FreeBoundaryOverlay';

interface ZoneDrawOverlayProps {
  viewport : Viewport;
  width    : number;
  height   : number;
  onSelect : (xMin: number, yMin: number, xMax: number, yMax: number) => void;
  onCancel : () => void;
}

/**
 * Full-screen drawing overlay.
 *
 * Click + drag to select a rectangular area in world coordinates.
 * Escape or the cancel button exits without selecting.
 *
 * Screen ↔ world mapping (Y-up, identical to WorldCanvas / ZoneOverlay):
 *   worldX = (screenX − w/2) / zoom + vp.x
 *   worldY = vp.y − (screenY − h/2) / zoom
 */
export const ZoneDrawOverlay: React.FC<ZoneDrawOverlayProps> = ({
  viewport, width, height, onSelect, onCancel,
}) => {
  const { t } = useTranslation();

  const canvasRef   = useRef<HTMLCanvasElement>(null);
  const vpRef       = useRef(viewport);
  vpRef.current     = viewport;
  const szRef       = useRef({ width, height });
  szRef.current     = { width, height };
  const startRef    = useRef<{ x: number; y: number } | null>(null);
  const endRef      = useRef<{ x: number; y: number } | null>(null);
  const selectRef   = useRef(onSelect);
  selectRef.current = onSelect;
  const cancelRef   = useRef(onCancel);
  cancelRef.current = onCancel;

  // ── Drawing ────────────────────────────────────────────────────
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width: w, height: h } = szRef.current;
    ctx.clearRect(0, 0, w, h);

    if (!startRef.current || !endRef.current) return;

    const rx = Math.min(startRef.current.x, endRef.current.x);
    const ry = Math.min(startRef.current.y, endRef.current.y);
    const rw = Math.abs(endRef.current.x - startRef.current.x);
    const rh = Math.abs(endRef.current.y - startRef.current.y);

    // World-coord annotation helpers
    const { x: vpX, y: vpY, zoom } = vpRef.current;
    const toWX = (px: number) => Math.round((px - w / 2) / zoom + vpX);
    const toWY = (py: number) => Math.round(vpY - (py - h / 2) / zoom);

    const wx1 = toWX(rx);
    const wx2 = toWX(rx + rw);
    const wy1 = toWY(ry + rh);
    const wy2 = toWY(ry);

    // Determine if the zone center is in the free area
    const wcx = (wx1 + wx2) / 2;
    const wcy = (wy1 + wy2) / 2;
    const dist = Math.sqrt(wcx * wcx + wcy * wcy);
    const isFree = dist > ZONE_FREE_RADIUS;

    const fillColor   = isFree ? 'rgba(80, 220, 100, 0.15)' : 'rgba(100, 200, 255, 0.12)';
    const strokeColor = isFree ? 'rgba(80, 220, 100, 0.9)'  : 'rgba(100, 200, 255, 0.9)';
    const textColor   = isFree ? 'rgba(80, 220, 100, 0.95)' : 'rgba(150, 225, 255, 0.95)';

    // Fill
    ctx.fillStyle = fillColor;
    ctx.fillRect(rx, ry, rw, rh);

    // Dashed border
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth   = 2;
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(rx, ry, rw, rh);
    ctx.setLineDash([]);

    if (rw > 40) {
      const freeTag = isFree ? '  ✓ FREE' : '';
      const label = `(${wx1}, ${wy1}) – (${wx2}, ${wy2})  ·  ${wx2 - wx1} × ${wy2 - wy1}${freeTag}`;
      ctx.font         = "11px 'Courier New', monospace";
      ctx.textBaseline = 'bottom';
      ctx.textAlign    = 'left';
      const lx = rx + 4;
      const ly = ry > 20 ? ry - 4 : ry + rh + 14;
      ctx.fillStyle = 'rgba(0,0,0,0.7)';
      ctx.fillText(label, lx + 1, ly + 1);
      ctx.fillStyle = textColor;
      ctx.fillText(label, lx, ly);
    }
  }, []);

  // ── Redraw when viewport or canvas size changes ────────────────
  useEffect(() => {
    redraw();
  }, [viewport, width, height, redraw]);

  // ── Pointer events (mouse) ─────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const toLocal = (e: MouseEvent) => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };

    const onDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const p = toLocal(e);
      startRef.current = p;
      endRef.current   = p;
      redraw();
      e.preventDefault();
    };

    const onMove = (e: MouseEvent) => {
      if (!startRef.current) return;
      endRef.current = toLocal(e);
      redraw();
    };

    const onUp = (e: MouseEvent) => {
      if (!startRef.current) return;
      const end = toLocal(e);
      commit(end.x, end.y);
    };

    canvas.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);

    return () => {
      canvas.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };
  }, [redraw]);

  // ── Pointer events (touch) ─────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const toLocal = (t: Touch) => {
      const r = canvas.getBoundingClientRect();
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    };

    const onTStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const p = toLocal(e.touches[0]);
      startRef.current = p;
      endRef.current   = p;
      redraw();
      e.preventDefault();
    };

    const onTMove = (e: TouchEvent) => {
      if (!startRef.current || e.touches.length !== 1) return;
      endRef.current = toLocal(e.touches[0]);
      redraw();
      e.preventDefault();
    };

    const onTEnd = (e: TouchEvent) => {
      if (!startRef.current || e.changedTouches.length === 0) return;
      const p = toLocal(e.changedTouches[0]);
      commit(p.x, p.y);
      e.preventDefault();
    };

    canvas.addEventListener('touchstart', onTStart, { passive: false });
    canvas.addEventListener('touchmove',  onTMove,  { passive: false });
    canvas.addEventListener('touchend',   onTEnd,   { passive: false });

    return () => {
      canvas.removeEventListener('touchstart', onTStart);
      canvas.removeEventListener('touchmove',  onTMove);
      canvas.removeEventListener('touchend',   onTEnd);
    };
  }, [redraw]);

  // ── Keyboard Escape ────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        startRef.current = null;
        endRef.current   = null;
        cancelRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Commit selection ───────────────────────────────────────────
  const commit = (ex: number, ey: number) => {
    if (!startRef.current) return;
    const { width: w, height: h } = szRef.current;
    const { x: vpX, y: vpY, zoom } = vpRef.current;
    const toWX = (px: number) => Math.round((px - w / 2) / zoom + vpX);
    const toWY = (py: number) => Math.round(vpY - (py - h / 2) / zoom);

    const screenX1 = Math.min(startRef.current.x, ex);
    const screenX2 = Math.max(startRef.current.x, ex);
    const screenY1 = Math.min(startRef.current.y, ey);
    const screenY2 = Math.max(startRef.current.y, ey);

    const xMin = toWX(screenX1);
    const xMax = toWX(screenX2);
    const yMin = toWY(screenY2);   // screen bottom → low world Y
    const yMax = toWY(screenY1);   // screen top    → high world Y

    startRef.current = null;
    endRef.current   = null;
    selectRef.current(xMin, yMin, xMax, yMax);
  };

  return (
    <>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{
          position     : 'absolute',
          top          : 0,
          left         : 0,
          zIndex       : 8,
          cursor       : 'crosshair',
          pointerEvents: 'all',
        }}
      />
      {/* Instruction banner */}
      <div style={{
        position   : 'absolute',
        top        : 16,
        left       : '50%',
        transform  : 'translateX(-50%)',
        zIndex     : 9,
        pointerEvents: 'none',
        whiteSpace : 'nowrap',
        fontSize   : '12px',
        color      : 'rgba(150, 225, 255, 0.95)',
        background : 'rgba(0,0,0,0.65)',
        padding    : '4px 14px',
        borderRadius: '4px',
        userSelect : 'none',
      }}>
        {t('zone.drawInstruction')}
      </div>
      {/* Cancel button */}
      <button
        style={{
          position  : 'absolute',
          top       : 12,
          right     : 16,
          zIndex    : 9,
          background: 'rgba(0,0,0,0.65)',
          color     : 'rgba(255,255,255,0.85)',
          border    : '1px solid rgba(255,255,255,0.25)',
          padding   : '4px 12px',
          fontSize  : '12px',
          cursor    : 'pointer',
          borderRadius: '3px',
        }}
        onClick={onCancel}
      >
        {t('zone.drawCancelBtn')}
      </button>
    </>
  );
};
